import { useState } from "react";
import {
  PartiesClient,
  PrincipalsClient,
  StreamsClient,
  type PrincipalSummary,
} from "../api/generated";
import { usePermissions } from "../auth/SessionContext";
import { Alert, Chip, Empty, Field, Panel, describeError, useAsync, when } from "../components/ui";

const principals = new PrincipalsClient("");
const partiesClient = new PartiesClient("");
const streamsClient = new StreamsClient("");

const LEVELS = ["NONE", "READ", "WRITE", "ADMIN"] as const;

/**
 * Console module: <b>Users &amp; access</b>.
 *
 * Admins pick module + level; they never type a capability string. The expansion to capabilities
 * happens once, server-side, at session build — this screen only ever shows what the API reports.
 */
export function UsersPage() {
  const { hasCapability, session } = usePermissions();
  const { data, error, loading, reload } = useAsync(() => principals.principals_List("user"));
  const [selected, setSelected] = useState<PrincipalSummary | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canAdmin = hasCapability("users:admin");
  const canGrant = hasCapability("grants:admin");
  const modules = Object.keys(session?.modules ?? {}).sort();

  async function setLevel(principal: PrincipalSummary, module: string, level: string) {
    setSaveError(null);
    try {
      await principals.principals_SetAccess(principal.id!, module, { level });
      setNotice(`${principal.name}: ${module} → ${level}`);
      await reload();
      setSelected((s) => (s ? { ...s, moduleLevels: { ...s.moduleLevels, [module]: level } } : s));
    } catch (e) {
      setSaveError(describeError(e));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Users &amp; access</h1>
          <p className="sub">
            Set an access level per module for each user. Changing a level ends that user's current
            session; the new level applies when they sign in again.
          </p>
        </div>
      </div>

      {error && <Alert kind="error">{error}</Alert>}
      {saveError && <Alert kind="error">{saveError}</Alert>}
      {notice && <Alert kind="ok">{notice}</Alert>}

      {canAdmin && <CreateUser onCreated={reload} />}

      <Panel title="Users">
        {loading ? <Empty>Loading…</Empty> : (data ?? []).length === 0 ? <Empty>No users yet.</Empty> : (
          <table>
            <thead>
              <tr>
                <th>Name</th><th>Email</th><th>Inbox</th><th>Audience scope</th><th>Status</th>
                <th>Last signed in</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data!.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td className="mono">{p.email}</td>
                  <td>
                    {p.partyKey
                      ? <span className="mono">{p.partyKey}</span>
                      : <span className="chip warn">no inbox</span>}
                  </td>
                  <td>{p.maxAudienceScope}</td>
                  <td>{p.isActive ? "active" : <span className="muted">deactivated</span>}</td>
                  <td className="muted">{when(p.lastAuthenticatedAt)}</td>
                  <td>
                    <button className="ghost small" onClick={() => setSelected(p)}>Access</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {selected && (
        <Panel
          title={`Module access — ${selected.name}`}
          actions={<button className="ghost small" onClick={() => setSelected(null)}>Close</button>}
        >
          <table>
            <thead><tr><th>Module</th><th style={{ width: 260 }}>Level</th></tr></thead>
            <tbody>
              {modules.map((module) => (
                <tr key={module}>
                  <td>{module}</td>
                  <td>
                    <select
                      disabled={!canAdmin}
                      value={selected.moduleLevels?.[module] ?? "NONE"}
                      onChange={(e) => setLevel(selected, module, e.target.value)}
                    >
                      {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {canAdmin && (
            <InboxBindingEditor
              key={selected.id}
              principal={selected}
              onSaved={async () => {
                await reload();
                setSelected(null);
              }}
            />
          )}

          {canGrant && <AudienceScopeEditor principal={selected} onSaved={reload} />}
        </Panel>
      )}
    </>
  );
}

function CreateUser({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [scope, setScope] = useState("self");
  const [partyKey, setPartyKey] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await principals.principals_Create({
        name,
        email,
        password,
        maxAudienceScope: scope,
        partyKey: partyKey || undefined,
      });
      setName(""); setEmail(""); setPassword(""); setPartyKey("");
      onCreated();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Add a user">
      {error && <Alert kind="error">{error}</Alert>}
      <div className="row">
        <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} /></Field>
        <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Password">
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Audience scope">
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="self">self — own party only</option>
            <option value="direct">direct — named individuals</option>
            <option value="group">group — granted audiences</option>
            <option value="tenant">tenant — anyone here</option>
          </select>
        </Field>
        <Field label="Party key (optional)">
          <input
            value={partyKey}
            onChange={(e) => setPartyKey(e.target.value)}
            placeholder="derived from the email"
          />
        </Field>
        <button onClick={submit} disabled={busy || !name || !email || password.length < 12}>Create</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        Audience scope is whom this user may address, separate from what they may do. Passwords must
        be at least 12 characters. A party and an inbox are created automatically — enter a party
        key to reuse an existing one, or change it later under Access.
      </p>
    </Panel>
  );
}

/**
 * The party a user reads as, and the streams its inbox sees.
 *
 * A person with no party has no inbox at all: the read path resolves an audience against a party,
 * so an account without one can sign in and can never be messaged. New users get one automatically;
 * this is where a wrong link gets fixed, or an existing party — someone already imported from
 * another system — gets picked up instead.
 */
function InboxBindingEditor({ principal, onSaved }: { principal: PrincipalSummary; onSaved: () => void }) {
  const binding = useAsync(() => principals.principals_InboxBinding(principal.id!), [principal.id]);
  const parties = useAsync(() => partiesClient.parties_List("user"));
  const streams = useAsync(() => streamsClient.streams_List());

  const [partyKey, setPartyKey] = useState(principal.partyKey ?? "");
  const [selectedStreams, setSelectedStreams] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const current = selectedStreams ?? binding.data?.streams ?? [];
  const known = (parties.data ?? []).some((p) => p.key === partyKey);

  async function save(nextPartyKey: string | null) {
    setBusy(true);
    setError(null);
    try {
      await principals.principals_SetParty(principal.id!, {
        partyKey: nextPartyKey ?? undefined,
        createIfMissing: true,
        streams: nextPartyKey ? current : undefined,
      });
      onSaved();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  }

  function toggleStream(key: string) {
    setSelectedStreams(current.includes(key) ? current.filter((s) => s !== key) : [...current, key]);
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
      <h3>Inbox</h3>
      {error && <Alert kind="error">{error}</Alert>}

      {!principal.partyKey && (
        <Alert kind="warn">
          This account has no party, so it cannot receive messages. Link one below.
        </Alert>
      )}

      <div className="row">
        <Field label="Party">
          <input
            value={partyKey}
            onChange={(e) => setPartyKey(e.target.value)}
            placeholder="u-name"
            list={`user-parties-${principal.id}`}
          />
          <datalist id={`user-parties-${principal.id}`}>
            {(parties.data ?? []).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </datalist>
        </Field>
        <button disabled={busy || !partyKey} onClick={() => save(partyKey)}>
          {known ? "Link to this party" : "Create and link"}
        </button>
        {principal.partyKey && (
          <button className="ghost" disabled={busy} onClick={() => save(null)}>Unlink</button>
        )}
      </div>

      {streams.data && streams.data.length > 0 && (
        <Field label="Streams this inbox sees">
          <div>
            {streams.data.map((s) => (
              <label key={s.key} className="checkbox" style={{ display: "inline-flex", marginRight: 14 }}>
                <input
                  type="checkbox"
                  checked={current.includes(s.key!)}
                  onChange={() => toggleStream(s.key!)}
                />
                <span>{s.name}</span>
              </label>
            ))}
          </div>
        </Field>
      )}

      {binding.data?.partyKey && (
        <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
          Reading as <span className="mono">{binding.data.partyKey}</span> on{" "}
          <Chip>{binding.data.channel}</Chip>
          {(binding.data.streams ?? []).length === 0
            ? " — subscribed to no stream, so the inbox stays empty."
            : ` — ${binding.data.streams!.join(", ")}.`}
        </p>
      )}

      <p className="muted" style={{ fontSize: 12, marginBottom: 0 }}>
        Relinking moves the subscriptions with it. Link a person to their own user party — linking
        them to a store would let them read that store's messages.
      </p>
    </div>
  );
}

function AudienceScopeEditor({ principal, onSaved }: { principal: PrincipalSummary; onSaved: () => void }) {
  const [scope, setScope] = useState(principal.maxAudienceScope ?? "self");
  const [groups, setGroups] = useState((principal.audienceGroups ?? []).join(", "));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save() {
    setError(null);
    setSaved(false);
    try {
      await principals.principals_SetAudienceScope(principal.id!, {
        maxAudienceScope: scope,
        audienceGroups: groups.split(",").map((g) => g.trim()).filter(Boolean),
      });
      setSaved(true);
      onSaved();
    } catch (e) {
      setError(describeError(e));
    }
  }

  return (
    <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--line)" }}>
      <h3>Audience scope</h3>
      {error && <Alert kind="error">{error}</Alert>}
      {saved && <Alert kind="ok">Saved. This user's existing sessions are now invalid.</Alert>}
      <div className="row">
        <Field label="Maximum scope">
          <select value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="self">self</option>
            <option value="direct">direct</option>
            <option value="group">group</option>
            <option value="tenant">tenant</option>
          </select>
        </Field>
        <Field label="Granted audience groups (comma separated)">
          <input value={groups} onChange={(e) => setGroups(e.target.value)} placeholder="ontario-stores" />
        </Field>
        <button className="ghost" onClick={save}>Save scope</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 8, marginBottom: 0 }}>
        Group membership is recorded at publish, so adding members to a group later does not give
        them messages already sent.
      </p>
    </div>
  );
}
